/*----------------------------------
- DOMAINING NEWS ARCHIVE ROW
----------------------------------*/

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import Icon from '@/client/components/ui/icon/Icon';

import {
    domainingNewsEditionPath,
    type PublishedNewsEditionCard,
} from '@uniquedomains/components/contracts/news/public';
import {
    getDomainingNewsCategoryShortLabel,
    splitDomainingNewsDateParts,
} from '@uniquedomains/components/news/presentation';

/*----------------------------------
- TYPES
----------------------------------*/

type DomainingNewsArchiveRowProps = {
    edition: PublishedNewsEditionCard;
};

/*----------------------------------
- COMPONENT
----------------------------------*/

/**
 * One earlier edition. The date, the headline and the meta line are three grid cells
 * so they sit on one desktop row and fold under the headline on a phone, from the
 * same markup. The card payload carries no reading time, so the meta line counts
 * sources only.
 */
export default function DomainingNewsArchiveRow({ edition }: DomainingNewsArchiveRowProps) {
    const { day, month } = splitDomainingNewsDateParts(edition.dateKey);
    const categories = edition.categoryCodes
        .slice(0, 2)
        .map(getDomainingNewsCategoryShortLabel)
        .join(', ');

    // No `aria-label` on the link: the row's own text (date, headline, categories, sources)
    // is the accessible name. A label that left the visible date out failed WCAG 2.5.3,
    // which breaks voice control ("click 29 July") for a link that reads that way.
    return (
        <a
            href={domainingNewsEditionPath(edition.dateKey)}
            class="grid grid-cols-[56px_minmax(0,1fr)] items-start gap-x-3.5 gap-y-[7px] border-b border-[rgba(27,29,30,0.1)] py-4 transition hover:bg-[#ffffff80] lg:grid-cols-[120px_minmax(0,1fr)_260px_40px] lg:items-center lg:gap-x-6 lg:gap-y-0 lg:py-[22px]"
            data-testid={`domaining-news-card-${edition.dateKey}`}
        >
            <span class="flex flex-col lg:flex-row lg:items-baseline lg:gap-1.5">
                <time
                    dateTime={edition.dateKey}
                    class="text-[22px] font-bold leading-none tabular-nums tracking-[-0.03em] text-[#1b1d1e] lg:text-[30px] lg:tracking-[-0.04em]"
                >
                    {day}
                </time>
                <span class="mt-[3px] text-[11px] font-bold uppercase tracking-[0.08em] text-[#6a695e] lg:mt-0 lg:text-xs">
                    {month}
                </span>
            </span>

            <span class="min-w-0 text-pretty text-[15.5px] font-bold leading-[1.3] tracking-[-0.015em] text-[#1b1d1e] lg:text-[19px] lg:tracking-[-0.02em]">
                {edition.headline}
            </span>

            <span class="col-start-2 flex flex-wrap items-center gap-x-2.5 gap-y-[3px] text-xs text-[#66696b] lg:col-start-3 lg:row-start-1 lg:flex-col lg:items-start lg:gap-0 lg:text-[13px]">
                <span class="lg:font-semibold lg:text-[#4a4d4e]">{categories}</span>
                <span aria-hidden="true" class="lg:hidden">
                    ·
                </span>
                <span>
                    {edition.sourceCount} {edition.sourceCount === 1 ? 'source' : 'sources'}
                </span>
            </span>

            <Icon
                name="arrow-right"
                size={18}
                class="hidden shrink-0 text-[#6a695e] lg:col-start-4 lg:row-start-1 lg:block"
            />
        </a>
    );
}
